<template lang="pug">
.input-dialog-vertical
  //- キーボード入力
  //- TODO: 現時点では問題データ上設定されていないが設定自体は可能な手書き＋数式キーボードの場合、表示がおかしくなる
  .input-dialog-vertical__keyboard(v-if='answerFormat.isKeyboard')
    //- キーボード入力選択
    .input-dialog-vertical__keyboard__input-type
      input.input-dialog-vertical__keyboard__input-type__input(
        type='radio',
        :id='getInputId(inputDialogType.KEYBOARD)',
        :value='inputDialogType.KEYBOARD',
        v-model='selectedInputType',
        @change='changeInputType'
      )
      label.input-dialog-vertical__keyboard__input-type__label(:for='getInputId(inputDialogType.KEYBOARD)')
        fa-icon(icon='keyboard')
    .input-dialog-vertical__keyboard__text-before(v-if='beforeText') {{ beforeText }}
    //- 数式キーボード
    text-field-math.input-dialog-vertical__keyboard__input(
      v-model='inputValue',
      :keyboardName='mathKeyboardName',
      v-if='useMathKeyboard'
    )
    //- 化学式キーボード
    text-field-science.input-dialog-vertical__keyboard__input(v-model='inputValue', v-if='useScienceKeyboard')
    .input-dialog-vertical__keyboard__text-after(v-if='afterText') {{ afterText }}
  //- 手書き入力
  .input-dialog-vertical__tegaki(v-if='answerFormat.isTegaki')
    //- 手書き入力選択
    .input-dialog-vertical__tegaki__input-type
      input.input-dialog-vertical__tegaki__input-type__input(
        type='radio',
        :id='getInputId(inputDialogType.TEGAKI)',
        :value='inputDialogType.TEGAKI',
        v-model='selectedInputType',
        @change='changeInputType'
      )
      label.input-dialog-vertical__tegaki__input-type__label(:for='getInputId(inputDialogType.TEGAKI)')
        fa-icon(icon='pen')
    .input-dialog-vertical__tegaki__text-before(v-if='beforeText') {{ beforeText }}
    //- 手書き
    text-field-tegaki.input-dialog-vertical__tegaki__input(
      v-model='inputValue',
      :lang='lang',
      :savedStrokes='savedStrokes',
      :canStandardKeyboard='answerFormat.canStandardKeyboard',
      :isEnglish='answerFormat.isEnglishTegaki',
      :isVertical='true',
      @set-stroke-data='setStrokeData',
      v-if='useTegaki'
    )
      template(#resultTitle)
        slot(name='tegakiResultTitle')
      template(#recognizeButtonLabel)
        slot(name='tegakiRecognizeButtonLabel')
    .input-dialog-vertical__tegaki__text-after(v-if='afterText') {{ afterText }}
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import TextFieldTegaki from '@/components/organisms/student/TextFieldTegaki.vue'
import TextFieldMath from '@/components/molecules/student/TextFieldMath.vue'
import TextFieldScience from '@/components/molecules/student/TextFieldScience.vue'
import { StrokePoint } from '@/models/StrokePoint'
import { LangType, InputDialogType } from '@/models/studyModel'
import { AnswerFormat } from '@/const/AnswerFormats'

/**
 * 手書き/ソフトウェアキーボード入力（縦書き）
 */
@Component({
  components: {
    TextFieldTegaki,
    TextFieldMath,
    TextFieldScience,
  },
})
export default class InputDialogVertical extends Vue {
  // 入力値
  @Prop()
  value!: string

  // 前方テキスト
  @Prop()
  beforeText?: string

  // 後方テキスト
  @Prop()
  afterText?: string

  // 使用入力方法
  @Prop()
  inputType!: InputDialogType

  // 入力形式
  @Prop()
  answerFormat!: AnswerFormat

  /**
   * 手書きダイアログ用
   */
  // MyScriptに渡す言語タイプ
  @Prop()
  lang?: LangType
  // 保存済みペンストローク
  @Prop()
  savedStrokes?: StrokePoint[][]

  /**
   * キーボード用
   */
  // 数学キーボード名
  @Prop()
  mathKeyboardName?: string

  // 入力値ゲッター・セッター
  get inputValue(): string {
    return this.value
  }
  set inputValue(value: string) {
    this.$emit('input', value)
  }

  // 入力方法ゲッターセッター
  get selectedInputType(): InputDialogType {
    return this.inputType
  }
  set selectedInputType(value: InputDialogType) {
    this.$emit('set-input-type', value)
  }

  // コンポーネント単位のユニークID取得
  private readonly UID = this.$uuid.v4()

  // inputタグの個別ID
  private getInputId(inputDialogType: InputDialogType): string {
    return `id${this.UID}_${inputDialogType}`
  }

  private get inputDialogType(): InputDialogType {
    return InputDialogType
  }

  // どの入力方式を使用するか
  private get useTegaki(): boolean {
    return this.selectedInputType === InputDialogType.TEGAKI
  }
  private get useMathKeyboard(): boolean {
    return this.selectedInputType === InputDialogType.KEYBOARD && !!this.answerFormat.isMathKeyboard
  }
  private get useScienceKeyboard(): boolean {
    return this.selectedInputType == InputDialogType.KEYBOARD && !!this.answerFormat.isScienceKeyboard
  }

  // ペンストローク情報をemit
  private setStrokeData(strokes: StrokePoint[][]) {
    this.$emit('set-stroke-data', strokes)
  }

  // 入力方法変更時は入力内容をクリアする
  private changeInputType() {
    this.$emit('input', '')
  }
}
</script>

<style lang="stylus" scoped>
.input-dialog-vertical
  &__keyboard, &__tegaki
    writing-mode horizontal-tb
    display flex
    align-items center

    &__input-type
      &__input
        opacity 0
        position absolute
      &__label
        display block
        margin 0
        padding 0 5px
        float left
        border 1px solid var(--color-study-input-dialog-button__border)
      &:after
        content ""
        display block
        clear left
      input[type="radio"]:checked
        & + label
          background-color var(--color-study-input-dialog-button-active__background)

  &__keyboard
    flex-direction row
    flex-wrap wrap
    margin-bottom 10px

    &__input-type
      margin-right 5px

    &__text-before
      margin-right 10px

    &__input
      margin-right 10px

  &__tegaki
    flex-direction column
    gap 5px
    margin-right 5px

    &__text-before, &__text-after
      writing-mode vertical-rl
</style>
